import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Shield, KeyRound, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const AccountRecovery = () => {
  const navigate = useNavigate();
  const [path, setPath] = useState("choose"); // choose | code | ai | reset | submitted
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [recoveryToken, setRecoveryToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // AI concierge state
  const [aiChallengeId, setAiChallengeId] = useState("");
  const [aiQuestions, setAiQuestions] = useState([]); // [{index, question}]
  const [aiAnswers, setAiAnswers] = useState({}); // {index: "answer"}
  const [aiRetryMessage, setAiRetryMessage] = useState("");
  const [aiEscalated, setAiEscalated] = useState(null); // {case_id, message} once escalated

  // Rotated-code-after-reset modal state
  const [rotatedCode, setRotatedCode] = useState("");
  const [rotatedAck, setRotatedAck] = useState(false);

  const submitWithCode = async () => {
    if (!email || !code) { toast.error("Email and code required"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/recovery/start`, {
        email: email.trim().toLowerCase(),
        recovery_code: code.trim().toUpperCase().replace(/\s/g, ""),
      });
      if (res.data?.recovery_token) {
        setRecoveryToken(res.data.recovery_token);
        setPath("reset");
        toast.success("Code verified. Set a new password.");
      } else {
        toast.error("Recovery did not succeed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Incorrect code");
    } finally {
      setLoading(false);
    }
  };

  const startAiConcierge = async () => {
    if (!email) { toast.error("Enter your email first"); return; }
    setLoading(true);
    setAiRetryMessage("");
    setAiEscalated(null);
    try {
      const res = await axios.post(`${API}/auth/recovery/ai-verify/start`, {
        email: email.trim().toLowerCase(),
      });
      if (res.data?.path === "ai_questions") {
        setAiChallengeId(res.data.challenge_id);
        setAiQuestions(res.data.questions || []);
        setAiAnswers({});
        toast.success("Two quick questions from your concierge — real fans always remember these.");
      } else if (res.data?.path === "manual_review") {
        setAiEscalated({ case_id: res.data.case_id, message: res.data.message });
        toast.info("Escalated to our team.");
      } else {
        toast.info(res.data?.message || "Check your email for next steps.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start AI verification");
    } finally {
      setLoading(false);
    }
  };

  const submitAiAnswers = async () => {
    if (aiQuestions.some((q) => !(aiAnswers[q.index] || "").trim())) {
      toast.error("Answer both questions");
      return;
    }
    setLoading(true);
    setAiRetryMessage("");
    try {
      const orderedAnswers = aiQuestions.map((q) => aiAnswers[q.index] || "");
      const res = await axios.post(`${API}/auth/recovery/ai-verify/answer`, {
        challenge_id: aiChallengeId,
        answers: orderedAnswers,
      });
      if (res.data?.path === "ai_verified") {
        setRecoveryToken(res.data.recovery_token);
        setPath("reset");
        toast.success("Verified! Set your new password.");
      } else if (res.data?.path === "ai_retry") {
        setAiRetryMessage(res.data.message || "Please try again.");
        toast.error(res.data.message || "Wrong answer");
      } else if (res.data?.path === "manual_review") {
        setAiEscalated({ case_id: res.data.case_id, message: res.data.message });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const completeReset = async () => {
    if (!newPassword || newPassword.length < 8) { toast.error("Password min 8 chars with 1 letter + 1 number"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/recovery/reset`, {
        recovery_token: recoveryToken,
        new_password: newPassword,
      });
      toast.success("Password reset. Sign in with your new password.");
      // Show the new recovery code in a proper modal (replaces the old code)
      if (res.data?.new_recovery_code) {
        setRotatedCode(res.data.new_recovery_code);
      } else {
        navigate("/auth");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="recovery-page">
      {/* Rotated Recovery Code modal — shown ONCE after a successful reset */}
      {rotatedCode && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="rotated-recovery-code-modal">
          <div className="max-w-lg w-full bg-[#0a0e1a] border-2 border-[#EC008C] rounded-2xl p-6 sm:p-8 shadow-[0_0_60px_rgba(255,51,0,0.4)]">
            <div className="text-center mb-5">
              <div className="w-14 h-14 rounded-full bg-[#EC008C]/15 border border-[#EC008C] flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🔐</span>
              </div>
              <h2 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">Your NEW Secret Account ID Code</h2>
              <p className="text-[#8A92A0] text-sm">
                Your old code is now invalid. <span className="text-[#EC008C] font-semibold">This new code replaces it</span>. Save it now — it cannot be retrieved later.
              </p>
            </div>
            <div className="bg-[#050810] border border-[#EC008C]/40 rounded-xl p-5 mb-5 text-center">
              <p className="font-mono text-2xl sm:text-3xl font-bold tracking-widest text-[#EC008C] break-all select-all" data-testid="rotated-recovery-code-value">
                {rotatedCode}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mb-5">
              <button
                onClick={() => { navigator.clipboard.writeText(rotatedCode); toast.success("Copied"); }}
                className="flex-1 px-4 py-2.5 rounded-lg bg-[#1A2030] hover:bg-[#1A2030]/70 text-white text-sm font-semibold"
                data-testid="copy-rotated-recovery-code-btn"
              >📋 Copy Code</button>
              <button
                onClick={() => {
                  const blob = new Blob([`StardomFans Secret Account ID Code (Rotated)\n\nEmail: ${email}\nCode: ${rotatedCode}\nIssued: ${new Date().toISOString()}\n\nKeep this code safe. It is the only way to recover your account if you lose access. Do not share it with anyone.`], { type: "text/plain" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `StardomFans-Recovery-Code.txt`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Downloaded");
                }}
                className="flex-1 px-4 py-2.5 rounded-lg bg-[#1A2030] hover:bg-[#1A2030]/70 text-white text-sm font-semibold"
                data-testid="download-rotated-recovery-code-btn"
              >⬇️ Download .txt</button>
            </div>
            <label className="flex items-start gap-3 mb-5 cursor-pointer">
              <input type="checkbox" checked={rotatedAck} onChange={(e) => setRotatedAck(e.target.checked)} className="mt-1 w-5 h-5 accent-[#EC008C]" data-testid="rotated-recovery-ack-checkbox" />
              <span className="text-[#8A92A0] text-sm">I have saved my new Secret Account ID Code. I understand it cannot be retrieved later.</span>
            </label>
            <button
              onClick={() => {
                if (!rotatedAck) { toast.error("Please confirm you've saved your code"); return; }
                setRotatedCode("");
                navigate("/auth");
              }}
              disabled={!rotatedAck}
              className={`w-full py-3 rounded-full font-bold text-base transition-all ${rotatedAck ? "bg-[#EC008C] text-black hover:bg-[#FF3399] hover:scale-[1.02]" : "bg-[#1A2030] text-[#6A7280] cursor-not-allowed"}`}
              data-testid="rotated-recovery-continue-btn"
            >Continue to Sign In</button>
          </div>
        </div>
      )}

      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2"><img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} /></Link>
          <Link to="/auth"><Button variant="ghost" className="text-[#8A92A0] hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Sign In</Button></Link>
        </div>
      </header>

      <main className="pt-28 max-w-xl mx-auto px-6 pb-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EC008C]/10 border border-[#EC008C]/30 mb-4">
            <Shield className="w-4 h-4 text-[#EC008C]" />
            <span className="text-sm text-[#EC008C] font-medium tracking-wide">ACCOUNT RECOVERY</span>
          </div>
          <h1 className="font-['Unbounded'] text-3xl sm:text-4xl font-bold mb-3" data-testid="recovery-title">Recover Your Account</h1>
          <p className="text-[#8A92A0] text-sm leading-relaxed">
            No email links. No external services. Use your Secret Account ID Code — or have our
            AI concierge verify who you are by asking things only you would know (Token wallet,
            KYC status, first purchase, etc.).
          </p>
        </div>

        {path === "choose" && (
          <div className="space-y-4" data-testid="recovery-choose">
            <button
              onClick={() => setPath("code")}
              className="w-full text-left card p-5 hover:border-[#EC008C] transition-all group"
              data-testid="path-code-btn"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#EC008C]/10 border border-[#EC008C]/30 flex items-center justify-center group-hover:bg-[#EC008C]/20 transition-colors">
                  <KeyRound className="w-6 h-6 text-[#EC008C]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white mb-1">I have my Secret Account ID Code</h3>
                  <p className="text-sm text-[#8A92A0]">Instant reset. Set a new password in under a minute.</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setPath("ai")}
              className="w-full text-left card p-5 hover:border-[#EC008C] transition-all group"
              data-testid="path-ai-btn"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#EC008C]/10 border border-[#EC008C]/30 flex items-center justify-center group-hover:bg-[#EC008C]/20 transition-colors">
                  <Sparkles className="w-6 h-6 text-[#EC008C]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white mb-1">I lost my code — verify with AI concierge</h3>
                  <p className="text-sm text-[#8A92A0]">Answer two questions only you would know. Verified instantly by AI. No passport, no ID.</p>
                </div>
              </div>
            </button>
          </div>
        )}

        {path === "code" && (
          <div className="card p-6 space-y-4" data-testid="recovery-code-form">
            <h2 className="font-['Unbounded'] text-xl font-bold text-white">Enter Your Recovery Details</h2>
            <div><Label>Email *</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="recovery-email" placeholder="you@example.com" /></div>
            <div>
              <Label>Secret Account ID Code *</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                className="font-mono tracking-widest"
                data-testid="recovery-code-input"
              />
              <p className="text-xs text-[#8A92A0] mt-1">16 characters with dashes, e.g. <span className="font-mono">XK7M-9P2Q-RB4N-2H8F</span></p>
            </div>
            <Button onClick={submitWithCode} disabled={loading} className="w-full bg-[#EC008C] text-black hover:bg-[#FF3399] font-bold rounded-full h-12" data-testid="recovery-code-submit">
              {loading ? "Verifying..." : "Verify Code"}
            </Button>
            <button onClick={() => setPath("choose")} className="text-[#8A92A0] hover:text-white text-sm w-full text-center">← Back to options</button>
          </div>
        )}

        {path === "ai" && (
          <div className="card p-6 space-y-4" data-testid="recovery-ai-form">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#EC008C]" />
              <h2 className="font-['Unbounded'] text-xl font-bold text-white">AI Identity Concierge</h2>
            </div>

            {aiEscalated ? (
              <div className="p-4 rounded-xl bg-[#0A0E18] border border-[#1A2030]" data-testid="recovery-ai-escalated">
                <p className="text-white font-semibold mb-1">Escalated to our team</p>
                <p className="text-[#8A92A0] text-sm">{aiEscalated.message}</p>
                <p className="text-[#6A7280] text-xs mt-2">Case #{aiEscalated.case_id}</p>
              </div>
            ) : aiQuestions.length === 0 ? (
              <>
                <p className="text-[#8A92A0] text-sm">Enter the email on your account. I&apos;ll ask two quick things only the real you would know — real fans always remember these.</p>
                <div><Label>Email on account *</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="recovery-ai-email" placeholder="you@example.com" /></div>
                <Button onClick={startAiConcierge} disabled={loading} className="w-full bg-[#EC008C] text-black hover:bg-[#FF3399] font-bold rounded-full h-12" data-testid="recovery-ai-start-btn">
                  {loading ? "Starting..." : "Start AI Concierge Verification"}
                </Button>
              </>
            ) : (
              <>
                <p className="text-[#8A92A0] text-sm">Answer these two questions honestly. Semantic matching allows for typos and phrasing — but wildly incorrect answers will fail.</p>
                {aiQuestions.map((q) => (
                  <div key={q.index} data-testid={`recovery-ai-question-${q.index}`}>
                    <Label>{q.question}</Label>
                    <Input
                      value={aiAnswers[q.index] || ""}
                      onChange={(e) => setAiAnswers({ ...aiAnswers, [q.index]: e.target.value })}
                      className="mt-1"
                      data-testid={`recovery-ai-answer-${q.index}`}
                      placeholder="Your answer"
                    />
                  </div>
                ))}
                {aiRetryMessage && (
                  <p className="text-[#EC008C] text-sm" data-testid="recovery-ai-retry-msg">{aiRetryMessage}</p>
                )}
                <Button onClick={submitAiAnswers} disabled={loading} className="w-full bg-[#EC008C] text-black hover:bg-[#FF3399] font-bold rounded-full h-12" data-testid="recovery-ai-submit-btn">
                  {loading ? "Verifying..." : "Submit Answers"}
                </Button>
              </>
            )}
            <button onClick={() => { setPath("choose"); setAiQuestions([]); setAiEscalated(null); setAiChallengeId(""); }} className="text-[#8A92A0] hover:text-white text-sm w-full text-center">← Back to options</button>
          </div>
        )}

        {path === "reset" && (
          <div className="card p-6 space-y-4" data-testid="recovery-reset-form">
            <h2 className="font-['Unbounded'] text-xl font-bold text-white">Set a New Password</h2>
            <p className="text-[#8DB87A] text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Identity verified</p>
            <div><Label>New Password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="8+ chars, 1 letter, 1 number" data-testid="recovery-new-password" /></div>
            <div><Label>Confirm New Password</Label><Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="recovery-confirm-password" /></div>
            <Button onClick={completeReset} disabled={loading} className="w-full bg-[#EC008C] text-black hover:bg-[#FF3399] font-bold rounded-full h-12" data-testid="recovery-reset-submit">
              {loading ? "Setting password..." : "Set New Password"}
            </Button>
          </div>
        )}

        {path === "submitted" && (
          <div className="card p-8 text-center" data-testid="recovery-submitted">
            <div className="w-16 h-16 rounded-full bg-[#8DB87A]/15 border-2 border-[#8DB87A] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#8DB87A]" />
            </div>
            <h2 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">Submitted for Review</h2>
            <p className="text-[#8A92A0] text-sm mb-6">
              Our admin team will review your identity verification within 24 hours. Once approved, you&apos;ll receive a notification with a one-time link to set your new password.
            </p>
            <Link to="/auth"><Button className="bg-[#EC008C] text-black hover:bg-[#FF3399] font-bold rounded-full px-6">Back to Sign In</Button></Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default AccountRecovery;
